import { NgZone } from '@angular/core';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { environment } from '@environments/environment';

let started = false;

/**
 * Routes that carry a credential or record id in the URL path. The agent strips the
 * query string but keeps the path, so the value is replaced before anything is sent.
 * Keep in step with the `:token` / `:report_id` routes in the apps' routing modules.
 */
export const PATH_VALUE_OBFUSCATION = {
  regex: /\/(invite|respect|reset-password|report|assessments\/complete)\/[^/?#\s"'\\]+/g,
  replacement: '/$1/:value'
};

/** Whether the monitoring agent is running on this page. */
export function isMonitoringStarted(): boolean {
  return started;
}

/**
 * Starts New Relic browser monitoring. Call only after the visitor has
 * consented; the agent loads immediately on instantiation and sets cookies.
 * It cannot be stopped once started, so a later withdrawal takes effect on
 * the next page load (the consent cookie is checked before calling this).
 *
 * The agent is created outside Angular's zone. Inside it, the agent's DOM observers
 * and timers trigger change detection, whose DOM updates re-fire those observers,
 * locking the page in an endless change detection loop.
 */
export function startMonitoring(applicationId: string, ngZone: NgZone): void {
  if (started) {
    return;
  }
  started = true;

  ngZone.runOutsideAngular(() => new BrowserAgent({
    info: {
      applicationID: applicationId,
      beacon: environment.new_relic.beaconUrl,
      errorBeacon: environment.new_relic.beaconUrl,
      licenseKey: environment.new_relic.licenseKey,
      sa: 1
    },
    init: {
      ajax: {
        deny_list: [
          environment.new_relic.beaconUrl
        ]
      },
      distributed_tracing: {
        enabled: true
      },
      obfuscate: [PATH_VALUE_OBFUSCATION],
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true
      },
      privacy: {
        cookies_enabled: true
      }
    },
    loader_config: {
      accountID: environment.new_relic.accountId,
      agentID: applicationId,
      applicationID: applicationId,
      licenseKey: environment.new_relic.licenseKey,
      trustKey: environment.new_relic.accountId
    }
  }));
}
